/**
 * Pure helpers for collaboration phase 3 (components/collab: SeePreview in the presence list, the handoff notes
 * and HandoffDialog, SuggestionEditor and SuggestionsPanel, ActivityPanel) over vfs-service's answers
 * (services/CollabService.js). Authors: `{ id: 'user:<sub>', sub, name, initials, slot }`.
 */
import { diffLinesFor } from './changesModel';
import { firstName } from './presenceModel';
import { matchesServices } from './activityServicesModel';

// ---- "See what X sees" ----

/** `services/web` or `web` → `web`. */
export const serviceNameOf = (service) => String(service || '').replace(/^services\//, '').replace(/\/+$/, '');

/** A route inside an app: starts with '/', at most 500 characters ('/' when none). */
export function cleanRoute(route) {
  const text = String(route || '').trim();
  if (!text) { return '/'; }
  return (text.startsWith('/') ? text : `/${text}`).slice(0, 500);
}

/**
 * The route the preview iframe shows, from its current URL and the preview's base URL (the proxy prefix, e.g.
 * `/api/preview/p/services/web/proxy/`): `/stores?city=Milano`. Null when the current URL is not under the base.
 */
export function routeFromUrls(currentUrl, baseUrl) {
  if (!currentUrl || !baseUrl) { return null; }
  let current;
  let base;
  try {
    current = new URL(currentUrl, 'http://flow.local');
    base = new URL(baseUrl, 'http://flow.local');
  } catch {
    return null;
  }
  if (current.origin !== base.origin) { return null; }
  const prefix = base.pathname.replace(/\/+$/, '');
  if (current.pathname !== prefix && !current.pathname.startsWith(`${prefix}/`)) { return null; }
  const rest = current.pathname.slice(prefix.length) || '/';
  // The preview's own share/auth query params are not part of the app's route.
  const params = new URLSearchParams(current.search);
  params.delete('flow_share');
  const search = params.toString();
  return cleanRoute(`${rest}${search ? `?${search}` : ''}${current.hash || ''}`);
}

/** The preview URL at `route`: the base (the proxy root) plus the route, the base's own query kept. */
export function joinRoute(baseUrl, route) {
  if (!baseUrl) { return null; }
  const clean = cleanRoute(route);
  let base;
  try {
    base = new URL(baseUrl, 'http://flow.local');
  } catch {
    return null;
  }
  const relative = !/^[a-z]+:\/\//i.test(baseUrl);
  const [pathAndQuery, hash = ''] = clean.split('#');
  const [path, search = ''] = pathAndQuery.split('?');
  const target = new URL(base.toString());
  target.pathname = `${base.pathname.replace(/\/+$/, '')}${path}`;
  const params = new URLSearchParams(base.search);
  new URLSearchParams(search).forEach((value, key) => params.set(key, value));
  target.search = params.toString() ? `?${params}` : '';
  target.hash = hash ? `#${hash}` : '';
  return relative ? `${target.pathname}${target.search}${target.hash}` : target.toString();
}

/** A person's latest preview view `{ service, route }`, or null when they are not looking at a preview right now. */
export function previewOfPerson(person) {
  const views = [...(person?.views || [])].sort((a, b) => (b.at || 0) - (a.at || 0));
  const latest = views[0];
  if (!latest || latest.kind !== 'preview') { return null; }
  return { service: latest.service || null, route: cleanRoute(latest.route) };
}

/** An agent's last preview `{ service, route }`, or null. */
export function previewOfAgent(agent) {
  if (!agent?.preview) { return null; }
  return { service: agent.preview.service || null, route: cleanRoute(agent.preview.route) };
}

/** "See what Sara sees", "See Flow chat's preview". */
export function seeLabel(entry, you) {
  if (entry?.driver || entry?.label) {
    const label = entry.label === 'Flow chat' || entry.client === 'flow-agent' ? 'Flow chat' : (entry.label || 'the agent');
    return `See ${label}’s preview${entry.driver && !(you && entry.driver.id === you.id) ? ` (for ${firstName(entry.driver.name)})` : ''}`;
  }
  return `See what ${firstName(entry?.name)} sees`;
}

// ---- Handoff notes ----

/** The chat suggestion for "Ask the assistant to draft it" (never sent on its own). */
export function draftPrompt(projectName, since) {
  const when = since ? new Date(since).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : 'I arrived';
  return `Draft my handoff note for ${projectName || 'this project'}: what I did since ${when} (use vfs_history for my changes and my agents'), what's next, and open questions. Keep it short, three headings: Done, Next, Open questions.`;
}

/** The first line or so of a note, for a banner or a notification. */
export function noteExcerpt(note, max = 140) {
  const text = String(note?.done || note?.next || note?.questions || '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Whether a person made changes in the project since `since` (their typing or their agents'), from `/collab/changes`. */
export function changedSince(changesAnswer, since) {
  const youId = changesAnswer?.you?.id;
  if (!youId) { return 0; }
  return (changesAnswer?.changes || []).filter((c) => c.author?.id === youId && (!since || String(c.timestamp) >= String(since))).length;
}

// ---- Suggestions ----

/** A suggestion's unified patch as diff lines (`{ kind, text, oldLine, newLine }`). */
export function patchLines(patch) {
  const text = String(patch || '');
  if (!text) { return []; }
  return diffLinesFor(text.startsWith('diff --git') ? text : `diff --git a/x b/x\n${text}`, null);
}

export const pendingCount = (answer) => (answer?.suggestions || []).filter((s) => s.status === 'pending').length;

/**
 * The lines of the current file a suggestion changes, `{ start, end }` (a deletion counts at the line where it was), or
 * null when the patch changes no line.
 */
export function suggestionLines(patch) {
  let start = null;
  let end = null;
  let next = 0;
  patchLines(patch).forEach((line) => {
    if (line.kind === 'hunk') {
      const match = /\+(\d+)/.exec(line.text);
      next = match ? Number(match[1]) : 0;
      return;
    }
    if (line.kind === 'ctx') { next = line.newLine + 1; return; }
    const at = line.kind === 'add' ? line.newLine : Math.max(1, next);
    if (line.kind === 'add') { next = line.newLine + 1; }
    start = start === null ? at : Math.min(start, at);
    end = end === null ? at : Math.max(end, at);
  });
  return start === null ? null : { start, end };
}

/** "line 14", "lines 14–18", or '' when the lines are not known. */
export const suggestionLinesLabel = (range) => (!range ? '' : range.start === range.end ? `line ${range.start}` : `lines ${range.start}–${range.end}`);

/** The one name of the entry point to the suggestions, everywhere: "Suggestions (2)", "Suggestions" when none wait. */
export const suggestionsLabel = (waiting = 0) => (waiting > 0 ? `Suggestions (${waiting})` : 'Suggestions');

// ---- Activity timeline ----

export const ACTIVITY_TYPES = [
  { value: 'session', label: 'Typing' },
  { value: 'agent-run', label: 'Agent runs' },
  { value: 'comment', label: 'Comments' },
  { value: 'mention', label: 'Mentions' },
  { value: 'revert', label: 'Reverts' },
  { value: 'pause', label: 'Pauses' },
  { value: 'handoff', label: 'Handoff notes' },
  { value: 'note', label: 'Notes' },
  { value: 'suggestion', label: 'Suggestions' },
  { value: 'share', label: 'Sharing' },
  { value: 'data', label: 'Data' },
  { value: 'origin', label: 'Templates and remixes' },
  { value: 'config', label: 'Secrets' },
  { value: 'mail', label: 'Emails' },
  { value: 'publish', label: 'Publishing' },
  { value: 'repo-flags', label: 'Repo flags' },
  { value: 'service-delete', label: 'Services deleted' },
];

/**
 * The event types of the project's data and origin (vfs-service, each with its `summary`): a change made in the Data tab
 * (`data-change`), the data restored to a restore point (`data-restore`), a restore point saved (`restore-point`), the
 * project made from a template (`template`) or remixed from another (`remix`). Their label in the feed.
 */
export const DATA_EVENT_LABELS = {
  'data-change': 'Data changed',
  'data-restore': 'Data restored',
  'restore-point': 'Restore point',
  template: 'From a template',
  remix: 'Remix',
  // A repo flagged Read-only / No build, or unflagged ("Alice set services/api read-only").
  'repo-flags': 'Repo flags',
  // A whole service deleted (Apps › Delete service…) or brought back; a delete has Undo while it is kept.
  'service-delete': 'Service deleted',
  'service-restore': 'Service restored',
};

/** The filter group a type belongs to (reply/resolve with comments, resume with pauses, decided suggestions with them). */
export function typeGroup(type) {
  if (type === 'reply' || type === 'resolve') { return 'comment'; }
  if (type === 'resume') { return 'pause'; }
  if (type === 'suggestion-accepted' || type === 'suggestion-rejected') { return 'suggestion'; }
  if (type === 'data-change' || type === 'data-restore' || type === 'restore-point') { return 'data'; }
  if (type === 'template' || type === 'remix') { return 'origin'; }
  if (type === 'deploy') { return 'publish'; }
  if (type === 'service-restore') { return 'service-delete'; }
  return type;
}

/** Who an event is about, for filters and swimlanes: `{ key, kind: 'person'|'agent', author, label, via }`. */
export function participantOf(event) {
  if (event?.agent) {
    const driver = event.agent.driver || event.actor || null;
    const label = event.agent.label || (event.agent.via === 'Flow' ? 'Flow chat' : event.agent.via) || 'AI agent';
    return { key: `agent:${label}|${driver?.id || ''}`, kind: 'agent', author: driver, label, via: event.agent.via || null };
  }
  if (event?.actor) { return { key: event.actor.id, kind: 'person', author: event.actor, label: event.actor.name, via: null }; }
  return null;
}

/**
 * The participants of some events, people first then agents, each with how many events. With `counted` (e.g. the
 * events of the services picked), everyone in `events` stays listed but the counts are those of `counted`.
 */
export function participantsOf(events, counted = null) {
  const byKey = new Map();
  if (counted) {
    (events || []).forEach((event) => {
      const who = participantOf(event);
      if (who && !byKey.has(who.key)) { byKey.set(who.key, { ...who, count: 0 }); }
    });
  }
  (counted || events || []).forEach((event) => {
    const who = participantOf(event);
    if (!who) { return; }
    const entry = byKey.get(who.key) || { ...who, count: 0 };
    entry.count += 1;
    byKey.set(who.key, entry);
  });
  return [...byKey.values()].sort((a, b) => (a.kind === b.kind ? b.count - a.count : a.kind === 'person' ? -1 : 1));
}

/**
 * Events left after the filters: `who` (participant keys, empty = all), `types` (type groups, empty = all), `only`
 * (all, people, agents, mine), `services` (keys of utils/activityServicesModel, empty = all services).
 */
export function filterEvents(events, { who = [], types = [], only = 'all', youId = null, services = [] } = {}) {
  return (events || []).filter((event) => {
    if (!matchesServices(event, services)) { return false; }
    const p = participantOf(event);
    if (only === 'people' && p?.kind !== 'person') { return false; }
    if (only === 'agents' && p?.kind !== 'agent') { return false; }
    if (only === 'mine' && !(youId && (p?.author?.id === youId))) { return false; }
    if (who.length > 0 && !(p && who.includes(p.key))) { return false; }
    if (types.length > 0 && !types.includes(typeGroup(event.type))) { return false; }
    return true;
  });
}

/**
 * Swimlane blocks over [from, to]: per participant, `{ key, left, width, kind: 'solid'|'hatch'|'dot', event }` in
 * percent. Typing sessions are solid, agent runs hatched, everything else a dot.
 */
export function laneBlocks(events, from, to) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  const span = Math.max(1, end - start);
  const pct = (iso) => Math.min(100, Math.max(0, ((Date.parse(iso) - start) / span) * 100));
  const lanes = new Map();
  (events || []).forEach((event) => {
    const p = participantOf(event);
    if (!p || !event.at) { return; }
    const lane = lanes.get(p.key) || { ...p, blocks: [] };
    if (event.type === 'session' || event.type === 'agent-run') {
      const a = pct(event.at);
      const b = pct(event.until || event.at);
      const left = Math.min(a, b);
      lane.blocks.push({ kind: event.type === 'session' ? 'solid' : 'hatch', left, width: Math.max(0.8, Math.abs(b - a)), event });
    } else {
      lane.blocks.push({ kind: 'dot', left: pct(event.at), width: 0, event });
    }
    lanes.set(p.key, lane);
  });
  return [...lanes.values()];
}

/**
 * What a typing session or an agent run did, as the feed shows it (vfs-service src/collab/describe.js gives
 * `description`: the commits' own subjects, an agent's `summary` of each write, else the files; `details`: up to 5
 * subjects, newest first): `{ who, description, more, counts }` — `who` "Claude Code for Guglielmo" / "Marta Rossi",
 * `more` the other subjects, `counts` "7 files (+476 −1)". Null for any other event, or one from a vfs-service that
 * does not describe it yet (the feed shows `summary` then).
 */
export function headlineOf(event) {
  if (!event || !['session', 'agent-run'].includes(event.type)) { return null; }
  const description = typeof event.description === 'string' ? event.description.trim() : '';
  if (!description) { return null; }
  const driver = event.agent?.driver || event.actor;
  const label = event.agent ? (event.agent.label || (event.agent.via === 'Flow' ? 'Flow chat' : event.agent.via) || 'AI agent') : null;
  const who = label ? `${label} for ${firstName(driver?.name)}` : (event.actor?.name || 'Someone');
  const more = (Array.isArray(event.details) ? event.details : [])
    .filter((line) => typeof line === 'string' && line.trim() && line.trim() !== description)
    .map((line) => line.trim());
  const files = event.files?.length || 0;
  const lines = Number.isFinite(event.added) && Number.isFinite(event.removed) ? ` (+${event.added} −${event.removed})` : '';
  return { who, description, more, counts: files ? `${files} file${files === 1 ? '' : 's'}${lines}` : '' };
}

/** One line for an event where there is room for one: "Claude Code for Guglielmo · Leaderboard sorted by Elo", else its summary. */
export function eventLine(event) {
  const headline = headlineOf(event);
  return headline ? `${headline.who} · ${headline.description}` : (event?.summary || '');
}

/**
 * Where an event's link goes: `{ to: 'changes', shas, runId }`, `{ to: 'blame', path }`, `{ to: 'comment', path, line,
 * threadId }`, `{ to: 'handoff', noteId }`, `{ to: 'suggestion', suggestionId, path }`, `{ to: 'file', path, line }` (a
 * note about a line), or null.
 */
export function linkOf(event) {
  if (!event) { return null; }
  switch (typeGroup(event.type)) {
    case 'session':
    case 'agent-run':
    case 'revert':
      return event.shas?.length || event.runId ? { to: 'changes', shas: event.shas || [], runId: event.runId || null, label: 'See the changes' } : null;
    case 'comment':
    case 'mention':
      return event.path ? { to: 'comment', path: event.path, line: event.line ?? null, threadId: event.threadId || null, label: 'Open the comment' } : null;
    case 'handoff':
      return event.noteId ? { to: 'handoff', noteId: event.noteId, label: 'Read the note' } : null;
    case 'note':
      return event.path ? { to: 'file', path: event.path, line: event.line ?? null, label: 'Open the file' } : null;
    case 'suggestion':
      return event.suggestionId ? { to: 'suggestion', suggestionId: event.suggestionId, path: event.path || null, label: 'Review the suggestion' } : null;
    case 'data':
      return event.type === 'data-change' ? null : { to: 'restore-points', pointId: event.restorePointId || event.pointId || null, label: 'Open restore points' };
    case 'config':
      return { to: 'view', view: { tab: 'config', focus: event.service ? { service: event.service } : null }, label: 'Open Configuration' };
    case 'mail':
      return { to: 'view', view: { tab: 'mail' }, label: 'Open Mail' };
    default:
      return null;
  }
}

/** A second link for events on one file: blame. */
export const blameLinkOf = (event) => (event?.path && ['session', 'agent-run', 'revert'].includes(event.type) ? { to: 'blame', path: event.path, label: 'Blame' } : null);

/** Time axis labels between from and to (5 ticks). */
export function axisTicks(from, to, count = 5) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) { return []; }
  // Over more than a day the ticks say the day too ("Tue 14:00").
  const days = end - start > 20 * 3600_000;
  return Array.from({ length: count }, (_, i) => {
    const at = new Date(start + ((end - start) * i) / (count - 1));
    const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return { left: (i / (count - 1)) * 100, label: days ? `${at.toLocaleDateString([], { weekday: 'short', day: 'numeric' })} ${time}` : time };
  });
}

/** The events that touch `[from, to]` (an event lasts from `at` to `until`, or is a moment at `at`). */
export function eventsWithin(events, from, to) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  return (events || []).filter((event) => {
    const at = Date.parse(event.at);
    const until = Date.parse(event.until || event.at);
    return Number.isFinite(at) && at <= end && (Number.isFinite(until) ? until : at) >= start;
  });
}

/** The span between two points of a timeline (fractions 0–1, either order) of `range`: `{ from, to }`. */
export function spanBetween(range, a, b) {
  const start = Date.parse(range.from);
  const width = Date.parse(range.to) - start;
  const [lo, hi] = [Math.max(0, Math.min(a, b)), Math.min(1, Math.max(a, b))];
  return { from: new Date(start + width * lo).toISOString(), to: new Date(start + width * hi).toISOString() };
}

/** The range of the events: `{ from, to }` (to: now at the latest). */
export function rangeOf(events, now = new Date()) {
  const times = (events || []).flatMap((e) => [e.at, e.until]).filter(Boolean).map((t) => Date.parse(t)).filter(Number.isFinite);
  if (times.length === 0) { return { from: new Date(now.getTime() - 3600_000).toISOString(), to: now.toISOString() }; }
  return { from: new Date(Math.min(...times)).toISOString(), to: new Date(Math.max(now.getTime(), ...times)).toISOString() };
}

/**
 * Where an activity event opens in the project's workspace views (components/workspace/WorkspaceView): an agent run on By person
 * (its changes, revertable), a change on the Timeline (its commit's diff), everything else (comments, shares, notes,
 * pauses) on Activity.
 */
export function historyTargetOf(event) {
  const group = typeGroup(event?.type);
  if (group === 'agent-run' && (event.shas?.length || event.runId)) {
    return { tab: 'person', focus: { shas: event.shas || [], runId: event.runId || null } };
  }
  if ((group === 'session' || group === 'revert') && event.shas?.length) {
    return { tab: 'timeline', focus: { sha: event.shas[0] } };
  }
  if (event?.type === 'data-restore' || event?.type === 'restore-point') {
    const point = event.restorePointId || event.pointId || null;
    return { tab: 'data', focus: { view: 'restore', ...(point ? { point } : {}) } };
  }
  return { tab: 'activity', focus: null };
}
